import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ShoppingCart, User, Menu, X, MapPin, LogOut, Package, Heart, Store, ChevronDown, Bot, ShieldCheck } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { useI18n } from "@/contexts/I18nContext";
import { useSiteSettings } from "@/contexts/SiteSettingsContext";
import LanguageSwitcher from "@/components/layout/LanguageSwitcher";

const FALLBACK_LOGO = "https://customer-assets.emergentagent.com/job_build-web-905/artifacts/cd4lv2zk_52a8ffae-0841-40cd-9c81-57806e3e0ff5.png";

const Header = () => {
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { t } = useI18n();
  const { settings } = useSiteSettings();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [openMenu, setOpenMenu] = useState(false);
  const [openAcc, setOpenAcc] = useState(false);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) navigate(`/products?q=${encodeURIComponent(q.trim())}`);
  };

  // Lock body + html scroll while the mobile menu is open — prevents background bleed-through
  // (including keyboard ArrowDown / programmatic scroll, which act on documentElement).
  useEffect(() => {
    const bodyOriginal = document.body.style.overflow;
    const htmlOriginal = document.documentElement.style.overflow;
    if (openMenu) {
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      document.body.style.overflow = bodyOriginal || "";
      document.documentElement.style.overflow = htmlOriginal || "";
    }
    return () => {
      document.body.style.overflow = bodyOriginal || "";
      document.documentElement.style.overflow = htmlOriginal || "";
    };
  }, [openMenu]);

  return (
    <>
    <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-md border-b border-border" data-testid="site-header">
      {/* top mini-bar */}
      <div className="bg-primary text-primary-foreground text-xs">
        <div className="container-fah flex items-center justify-between py-1.5">
          <div className="flex items-center gap-2">
            <MapPin className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t("nav.delivery")}</span>
            <span className="sm:hidden">{t("nav.delivery.short")}</span>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/crop-advisor" className="hidden sm:inline-flex items-center gap-1 hover:underline" data-testid="link-advisor-top"><Bot className="w-3.5 h-3.5" /> {t("nav.advisor")}</Link>
            <Link to="/seller/register" className="hover:underline" data-testid="link-sell-top">{t("nav.sell")}</Link>
            <LanguageSwitcher compact />
          </div>
        </div>
      </div>

      {/* main row */}
      <div className="container-fah py-2.5 sm:py-3">
        <div className="flex items-center gap-2 sm:gap-6">
          <Link to="/" className="flex items-center gap-2 shrink-0 min-w-0" data-testid="link-home-logo">
            <img src={settings.logo_url || FALLBACK_LOGO} alt={settings.site_name} className="h-9 w-9 sm:h-12 sm:w-12 object-contain" />
            <div className="hidden sm:block leading-tight">
              <div className="font-display font-extrabold text-primary text-lg">{settings.site_name}</div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{settings.short_tagline}</div>
            </div>
            <div className="sm:hidden font-display font-extrabold text-primary text-sm truncate max-w-[100px]">{settings.site_name}</div>
          </Link>

          {/* Desktop search */}
          <form onSubmit={onSearch} className="flex-1 max-w-2xl hidden md:block min-w-0" data-testid="form-search">
            <div className="flex items-center bg-muted/60 rounded-lg border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition">
              <Search className="w-4 h-4 text-muted-foreground ml-3 shrink-0" />
              <input
                data-testid="input-search"
                type="text"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("search.placeholder")}
                className="flex-1 min-w-0 bg-transparent px-3 py-2.5 text-sm focus:outline-none"
              />
              <button type="submit" className="bg-secondary hover:opacity-90 text-secondary-foreground font-semibold px-4 py-2 rounded-md mr-1 shrink-0" data-testid="btn-search-submit">
                {t("search.button")}
              </button>
            </div>
          </form>

          <div className="flex items-center gap-0.5 sm:gap-2 shrink-0 ml-auto">
          {user ? (
            <div className="relative">
              <button onClick={() => setOpenAcc((s) => !s)} className="flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-muted text-sm" data-testid="btn-account-menu">
                <User className="w-5 h-5" />
                <span className="hidden md:inline font-medium">{(user.name || "Account").split(" ")[0]}</span>
                <ChevronDown className="w-3 h-3 hidden md:inline" />
              </button>
              {openAcc && (
                <div className="absolute right-0 mt-1 w-56 bg-white border border-border rounded-xl shadow-lg py-2 z-50" onMouseLeave={() => setOpenAcc(false)}>
                  <div className="px-4 py-2 border-b border-border">
                    <div className="font-semibold text-sm">{user.name}</div>
                    <div className="text-xs text-muted-foreground">{user.email}</div>
                    <div className="mt-1 inline-block text-[10px] px-2 py-0.5 rounded-full bg-accent text-accent-foreground uppercase tracking-wide">{user.role}</div>
                  </div>
                  {user.role === "seller" ? (
                    <Link to="/seller/dashboard" onClick={() => setOpenAcc(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm" data-testid="link-seller-dash"><Store className="w-4 h-4" /> {t("Seller Dashboard")}</Link>
                  ) : (
                    <Link to="/dashboard" onClick={() => setOpenAcc(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm" data-testid="link-buyer-dash"><Package className="w-4 h-4" /> {t("My Orders")}</Link>
                  )}
                  <Link to="/wishlist" onClick={() => setOpenAcc(false)} className="flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm" data-testid="link-wishlist"><Heart className="w-4 h-4" /> {t("Wishlist")}</Link>
                  <button onClick={() => { setOpenAcc(false); logout(); navigate("/"); }} className="w-full flex items-center gap-2 px-4 py-2 hover:bg-muted text-sm text-destructive" data-testid="btn-logout"><LogOut className="w-4 h-4" /> {t("Logout")}</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" className="px-3 py-2 text-sm font-medium hover:bg-muted rounded-lg" data-testid="link-login">{t("nav.login")}</Link>
          )}

          <Link to="/cart" className="relative flex items-center gap-1.5 px-2 sm:px-3 py-2 rounded-lg hover:bg-muted" data-testid="link-cart">
            <ShoppingCart className="w-5 h-5" />
            <span className="hidden sm:inline text-sm font-medium">{t("nav.cart")}</span>
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 sm:static sm:ml-1 bg-secondary text-secondary-foreground text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1" data-testid="badge-cart-count">
                {count}
              </span>
            )}
          </Link>

          <button onClick={() => setOpenMenu((s) => !s)} className="md:hidden p-1.5 rounded-lg hover:bg-muted" data-testid="btn-mobile-menu">
            {openMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
        </div>

        {/* Mobile search — separate row */}
        <form onSubmit={onSearch} className="md:hidden mt-2.5" data-testid="form-search-mobile">
          <div className="flex items-center bg-muted/60 rounded-lg border border-border focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition">
            <Search className="w-4 h-4 text-muted-foreground ml-3 shrink-0" />
            <input
              data-testid="input-search-mobile"
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={t("search.placeholder")}
              className="flex-1 min-w-0 bg-transparent px-3 py-2 text-sm focus:outline-none"
            />
            <button type="submit" className="bg-secondary hover:opacity-90 text-secondary-foreground font-semibold px-3 py-1.5 rounded-md mr-1 text-sm shrink-0" data-testid="btn-search-submit-mobile">
              {t("search.button")}
            </button>
          </div>
        </form>
      </div>

      {/* nav row (categories) */}
      <nav className="hidden md:block border-t border-border bg-white">
        <div className="container-fah flex items-center gap-6 py-2 text-sm overflow-x-auto">
          <Link to="/categories" className="font-semibold text-primary flex items-center gap-1" data-testid="nav-all-cat"><Menu className="w-3.5 h-3.5" /> {t("nav.all")}</Link>
          <Link to="/brands" className="text-muted-foreground hover:text-primary whitespace-nowrap flex items-center gap-1" data-testid="nav-brands"><Store className="w-3.5 h-3.5" /> {t("Brands")}</Link>
          {["seeds", "pesticides", "bio-fertilizers", "irrigation-products", "sprayers", "farm-machinery", "agriculture-tools"].map((s) => (
            <Link key={s} to={`/category/${s}`} className="text-muted-foreground hover:text-primary whitespace-nowrap capitalize" data-testid={`nav-${s}`}>
              {t(s.replace(/-/g, " "))}
            </Link>
          ))}
          <Link to="/crop-advisor" className="ml-auto text-secondary font-semibold flex items-center gap-1" data-testid="nav-advisor"><Bot className="w-3.5 h-3.5" /> {t("nav.advisor")}</Link>
        </div>
      </nav>

      {/* nav row and mobile menu are moved below outside header to avoid backdrop-blur containing-block trap */}
    </header>

    {/* mobile menu — fixed drawer OUTSIDE header (backdrop-blur on header would trap position:fixed) */}
    {openMenu && (
      <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-menu-overlay">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setOpenMenu(false)}
          data-testid="mobile-menu-backdrop"
        />
        {/* Drawer — 320×600 as requested */}
        <div
          className="absolute right-0 top-0 w-80 h-[600px] max-h-[100vh] bg-white shadow-2xl flex flex-col animate-in slide-in-from-right duration-200"
          data-testid="mobile-menu"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
            <div className="flex items-center gap-2">
              <img src={settings.logo_url || FALLBACK_LOGO} alt={settings.site_name} className="h-8 w-8 object-contain" />
              <span className="font-display font-bold text-sm">{settings.site_name || "FarmersAgroHub"}</span>
            </div>
            <button
              onClick={() => setOpenMenu(false)}
              className="p-1.5 rounded-lg hover:bg-muted"
              data-testid="btn-mobile-menu-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-3 grid gap-1 text-sm">
            {user ? (
              <>
                <Link
                  onClick={() => setOpenMenu(false)}
                  to={user.role === "seller" ? "/seller/dashboard" : user.role === "admin" ? "/admin" : "/dashboard"}
                  className="py-2.5 flex items-center gap-2 font-semibold text-primary"
                  data-testid="mobile-nav-account"
                >
                  <User className="w-4 h-4" /> {t("My Account")}
                </Link>
                {user.role === "buyer" && (
                  <Link
                    onClick={() => setOpenMenu(false)}
                    to="/dashboard?tab=orders"
                    className="py-2.5 flex items-center gap-2"
                    data-testid="mobile-nav-orders"
                  >
                    <Package className="w-4 h-4" /> {t("My Orders")}
                  </Link>
                )}
                {user.role === "seller" && (
                  <Link
                    onClick={() => setOpenMenu(false)}
                    to="/seller/dashboard?tab=orders"
                    className="py-2.5 flex items-center gap-2"
                    data-testid="mobile-nav-orders"
                  >
                    <Package className="w-4 h-4" /> {t("My Orders")}
                  </Link>
                )}
              </>
            ) : (
              <Link
                onClick={() => setOpenMenu(false)}
                to="/login"
                className="py-2.5 flex items-center gap-2 font-semibold text-primary"
                data-testid="mobile-nav-login"
              >
                <User className="w-4 h-4" /> {t("Login / Sign Up")}
              </Link>
            )}
            <Link onClick={() => setOpenMenu(false)} to="/brands" className="py-2.5 flex items-center gap-2" data-testid="mobile-nav-brands"><Store className="w-4 h-4" /> {t("Brands")}</Link>
            <Link onClick={() => setOpenMenu(false)} to="/crop-advisor" className="py-2.5 flex items-center gap-2 text-secondary font-semibold"><Bot className="w-4 h-4" /> {t("nav.advisor")}</Link>
            <Link onClick={() => setOpenMenu(false)} to="/products" className="py-2.5">{t("All Products")}</Link>
            <Link onClick={() => setOpenMenu(false)} to="/about" className="py-2.5">{t("nav.about")}</Link>
            <Link onClick={() => setOpenMenu(false)} to="/contact" className="py-2.5">{t("nav.contact")}</Link>
            <Link onClick={() => setOpenMenu(false)} to="/seller/register" className="py-2.5">{t("nav.sell")}</Link>
            {user && (
              <button
                onClick={() => { setOpenMenu(false); logout(); navigate("/"); }}
                className="py-2.5 flex items-center gap-2 text-destructive text-left border-t border-border mt-2 pt-3"
                data-testid="mobile-nav-logout"
              >
                <LogOut className="w-4 h-4" /> {t("Logout")}
              </button>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
};

export default Header;
